const DAY = 1440
export const planningError = message => Object.assign(new Error(message), { userFacing: true })

export function clockMinutes(value) {
  return typeof value === "string" && /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(value) ? Number(value.slice(0, 2)) * 60 + Number(value.slice(3)) : null
}

function dateAfter(date, days) {
  return new Date(Date.parse(`${date}T00:00:00Z`) + days * DAY * 60000).toISOString().slice(0, 10)
}

// Resolve civil times explicitly in the organization's zone, including DST.
export function scheduleContext({ startTime, date, timeZone }) {
  if (clockMinutes(startTime) == null) throw planningError("Enter a departure time before calculating or auto sorting.")
  const formatter = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
  const parts = timestamp => Object.fromEntries(formatter.formatToParts(new Date(timestamp)).map(part => [part.type, part.value]))
  const local = (day, clock) => {
    const target = Date.parse(`${day}T${clock}:00Z`)
    let timestamp = target
    for (let i = 0; i < 4; i++) {
      const p = parts(timestamp)
      const rendered = Date.parse(`${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}:00Z`)
      if (rendered === target) {
        // Repeated fall-back times use their earlier occurrence, matching Rails.
        for (const offset of [120, 90, 60, 30, 0]) {
          const earlier = timestamp - offset * 60000, q = parts(earlier)
          if (Date.parse(`${q.year}-${q.month}-${q.day}T${q.hour}:${q.minute}:00Z`) === target) return earlier / 60000
        }
      }
      timestamp += target - rendered
    }
    throw planningError(`${clock} does not exist on ${day} in ${timeZone}. Choose another time.`)
  }
  const start = local(date, startTime)
  const windows = new Map()
  const grouped = new WeakMap()
  return {
    start, date, timeZone,
    window(stop) {
      if (stop.windows?.length) {
        if (grouped.has(stop)) return grouped.get(stop)
        const constraints = stop.windows.map(window => this.window(window))
        const window = { open: Math.max(...constraints.map(window => window.open)), close: Math.min(...constraints.map(window => window.close)) }
        grouped.set(stop, window)
        return window
      }
      const key = JSON.stringify([stop.windowStart, stop.windowEnd])
      if (!windows.has(key)) {
        const from = clockMinutes(stop.windowStart), until = clockMinutes(stop.windowEnd)
        let startDay = date, endDay = date
        if (from != null && until != null && until < from) {
          if (clockMinutes(startTime) <= until) startDay = dateAfter(date, -1)
          else endDay = dateAfter(date, 1)
        }
        windows.set(key, { open: from == null ? -Infinity : local(startDay, stop.windowStart), close: until == null ? Infinity : local(endDay, stop.windowEnd) })
      }
      return windows.get(key)
    }
  }
}

export function scheduleRun(stops, { context, legs = [] }) {
  let cursor = context.start, driving = 0, waiting = 0, service = 0, distance = 0, lateStops = 0, lateness = 0
  const visits = stops.map((stop, index) => {
    const leg = legs[index]
    if (cursor == null || !leg || !Number.isFinite(leg.durationMillis) || leg.durationMillis < 0 || !Number.isFinite(leg.distanceMeters) || leg.distanceMeters < 0) {
      cursor = null
      return { key: stop.key, unknown: true }
    }
    const drive = leg.durationMillis / 60000
    const arrival = cursor + drive
    const window = context.window(stop)
    const begins = Math.max(arrival, window.open)
    const minutes = Number.isFinite(stop.serviceMinutes) && stop.serviceMinutes >= 0 ? stop.serviceMinutes : 5
    const departure = begins + minutes
    const late = Math.max(0, departure - window.close)
    driving += drive
    waiting += begins - arrival
    service += minutes
    distance += leg.distanceMeters
    if (late > 1 / 60) { lateStops++; lateness += late }
    cursor = departure
    return { key: stop.key, arrival, begins, departure, waiting: begins - arrival, service: minutes, late, arrivalLate: Math.max(0, arrival - window.close) }
  })
  return { visits, start: context.start, finish: cursor, driving, waiting, service, distance, lateStops, lateness, incomplete: visits.filter(visit => visit.unknown).length }
}

export function scheduleScore(schedule, method = "finish") {
  const secondary = { finish: schedule.finish - schedule.start, distance: schedule.distance, waiting: schedule.waiting }[method]
  return [schedule.incomplete, schedule.lateStops, schedule.lateness, secondary, schedule.finish - schedule.start, schedule.distance]
}

export function compareSchedules(a, b, method) {
  const left = scheduleScore(a, method), right = scheduleScore(b, method)
  for (let i = 0; i < left.length; i++) if (Math.abs(left[i] - right[i]) > 0.0001) return left[i] - right[i]
  return 0
}

export function formatRunTime(minutes, { date, timeZone }) {
  if (!Number.isFinite(minutes)) return "Unavailable"
  const instant = new Date(Math.ceil(minutes - 0.000001) * 60000)
  const clock = new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(instant)
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(instant).map(part => [part.type, part.value]))
  const days = Math.round((Date.parse(`${p.year}-${p.month}-${p.day}T00:00:00Z`) - Date.parse(`${date}T00:00:00Z`)) / 86400000)
  return `${clock}${days ? ` (${days > 0 ? "+" : ""}${days} day${Math.abs(days) === 1 ? "" : "s"})` : ""}`
}

export function receivingHours(stop) {
  const { windowStart: from, windowEnd: until } = stop
  if (from && until) return `${from}–${until}${until < from ? " (overnight)" : ""}`
  return from ? `from ${from}` : until ? `until ${until}` : "Unrestricted"
}
